import { Category } from 'src/interfaces/category.model';
import { Drink } from 'src/interfaces/drink.model';
import { Food } from 'src/interfaces/food.model';
import { Labels } from 'src/interfaces/enums.model';
import { PREMIERE_CATEGORY } from '../utils/premiere-category';

/** Позиция меню на десктопе: блюдо или напиток и стоп-лист */
export interface MenuItem {
  kind: 'food' | 'drink';
  item: Food | Drink;
  stoplist: boolean;
}

/** Раздел меню: категория и её позиции */
export interface MenuSection {
  category: Category;
  items: MenuItem[];
}

/** Ширина окна, с которой меню по ссылке заведения — десктопное */
export const DESKTOP_QUERY = '(min-width: 1024px)';

/** id элемента раздела — для прокрутки по вкладкам */
export function sectionAnchor(category: Category): string {
  return 'section-' + category.id;
}

/**
 * Все позиции меню и разделы — как в мобильной вёрстке: активные категории
 * по index, внутри — сначала блюда, затем напитки, по index; стоп-лист остаётся
 * в разделе (приглушён)
 */
export function buildMenu(
  categories: Category[],
  foods: Food[],
  drinks: Drink[],
  stoplistFoods: string[],
  stoplistDrinks: string[],
): { items: MenuItem[]; sections: MenuSection[]; premiere: MenuSection | null } {
  const order = new Map(categories.map((c) => [c.id, c.index]));
  const byIndex = (a: MenuItem, b: MenuItem) => a.item.index - b.item.index;
  const items: MenuItem[] = [
    ...foods.map((food) => ({ kind: 'food' as const, item: food, stoplist: stoplistFoods.includes(food.id) })).sort(byIndex),
    ...drinks.map((drink) => ({ kind: 'drink' as const, item: drink, stoplist: stoplistDrinks.includes(drink.id) })).sort(byIndex),
  ];

  const sections = categories
    .filter((category) => category.active)
    .sort((a, b) => a.index - b.index)
    .map((category) => ({ category, items: items.filter((i) => i.item.category === category.id) }))
    .filter((section) => section.items.length);

  // Новинки — без стоп-листа, в порядке меню
  const premiereItems = items
    .filter((i) => !i.stoplist && i.item.labels.includes(Labels.premiere) && order.has(i.item.category))
    .sort((a, b) => (order.get(a.item.category) ?? 0) - (order.get(b.item.category) ?? 0) || byIndex(a, b));
  const premiere = premiereItems.length ? { category: PREMIERE_CATEGORY, items: premiereItems } : null;

  return { items, sections, premiere };
}
